import React, { useState } from "react";
import { useSelector } from "react-redux";
import { formatDate } from "../../../utils/dateUtils";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import EditItemForm from "./EditItemForm";
import ItemAuditLogs from "../AuditLog/ItemAuditLogs";
import QRCode from "../../common/QRCode";
import ItemThumbnail from "../../common/ItemThumbnail";
import FloatingMenu from "../../common/FloatingMenu";
import { EditIcon, LogIcon, DeleteIcon } from "../../common/Icons";

const ViewItem = ({
  selectedItem,
  onClose,
  isEditMode,
  onEdit,
  onSave,
  onUpdate,
  onCancel,
  onDeleteItem,
  onShowAuditLogs,
  editFormRef,
}) => {
  const sections = useSelector((s) => s.careplan.sections);
  const item = useSelector((state) =>
    state.careplan.items.find((i) => i.id === selectedItem.id)
  );

  const [showAuditLogs, setShowAuditLogs] = useState(false);

  // Add null check at the start
  if (!selectedItem) {
    onClose(); // Close modal if item is undefined
    return null;
  }

  // Build the floating menu items, Menu items - ONLY for view mode
  const menuItems = !isEditMode
    ? [
        {
          label: "Edit",
          icon: <EditIcon color="#004F96" />,
          action: onEdit,
        },
        {
          label: "Audit log",
          icon: <LogIcon color="#004F96" />,
          action: () => setShowAuditLogs(true),
        },
        {
          label: "Delete",
          icon: <DeleteIcon color="#004F96" />,
          action: () => onDeleteItem(item),
        },
      ]
    : null;

  return (
    <>
      <FullScreenOverlay
        title={isEditMode ? "Edit Item" : "View Item"}
        iconClose={true}
        onClose={isEditMode ? onCancel : onClose} // Different handlers based on mode
        width="100%"
        height="100%"
        buttons={[]}
      >
        {isEditMode ? (
          <EditItemForm
            ref={editFormRef}
            item={selectedItem}
            initialSectionAnswers={selectedItem.section_overrides || {}}
            onUpdate={(updatedItem) => {
              onUpdate(updatedItem); // Goes to handleEditFormSave
              onSave(); // Triggers form submission
            }}
            onCancel={onCancel}
          />
        ) : (
          <FormSection>
            <>
              <ItemSection>
                <ItemLabel>Item title</ItemLabel>
                <ItemText>{item.title}</ItemText>
              </ItemSection>
              <ItemSection>
                <ItemLabel>Item image</ItemLabel>
                <ItemThumbnail
                  src={item?.image_url}
                  alt={item?.image_alt_text || item?.title}
                  width={"150px"}
                  height={"150px"}
                  backgroundColor={"#d9d9d9"}
                />
                {/* {item.image_url ? (
                <img src={item.image_url} alt={item.title} />
              ) : (
                <div></div>
              )}
            </ItemThumbnail> */}
              </ItemSection>
              <ItemSection>
                <ItemLabel>Item description</ItemLabel>
                <ItemText>{item.description}</ItemText>
              </ItemSection>
              <ItemSection>
                <ItemLabel>Item help</ItemLabel>
                {item.section_prompt_overrides &&
                  Object.entries(item.section_prompt_overrides).map(
                    ([secId, prompt]) => {
                      const sec = sections.find((s) => s.id === Number(secId));
                      return (
                        <ItemSection key={`prompt-${secId}`}>
                          <ItemLabel>
                            {sec ? sec.title : `Section ${secId}`} Prompt
                          </ItemLabel>
                          <ItemText>{prompt}</ItemText>
                        </ItemSection>
                      );
                    }
                  )}
              </ItemSection>
              <ItemSection>
                <ItemLabel>Answers</ItemLabel>
                {item.section_overrides &&
                  Object.entries(item.section_overrides).map(
                    ([secId, answers]) => {
                      const sec = sections.find((s) => s.id === Number(secId));
                      return (
                        <ItemSection key={secId}>
                          <ItemLabel>
                            {sec ? sec.title : `Section ${secId}`}
                          </ItemLabel>
                          {answers.map((ans, i) => (
                            <ItemText key={i}>{ans}</ItemText>
                          ))}
                        </ItemSection>
                      );
                    }
                  )}
              </ItemSection>
              <ItemSection>
                <QRCode resourceLink={item.resource_link} />
              </ItemSection>
              <ItemSection>
                <ItemInfo>
                  <ItemInfoSecftion>
                    <ItemInfoLabel>Created by </ItemInfoLabel>
                    <ItemText>{item.updated_by_name}</ItemText>
                  </ItemInfoSecftion>
                  <ItemInfoSecftion>
                    <ItemInfoLabel>Last updated </ItemInfoLabel>
                    <ItemText>{formatDate(item.last_updated_at)}</ItemText>
                  </ItemInfoSecftion>
                </ItemInfo>
              </ItemSection>
            </>
          </FormSection>
        )}
        {/* Only show floating menu in view mode */}
        {!isEditMode && (
          <FloatingMenu
            items={menuItems}
            position="bottom-center"
            offset={24}
          />
        )}
      </FullScreenOverlay>
      {showAuditLogs && (
        <ItemAuditLogs
          itemId={item.id}
          itemTitle={item.title}
          onClose={() => setShowAuditLogs(false)}
        />
      )}
    </>
  );
};

const FormSection = styled.div`
  width: 60%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-100, #fff);
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
`;

const ItemSection = styled.div`
  margin-bottom: 24px;
`;
const ItemLabel = styled.div.attrs({
  className: "body-text-regular regular neutral-600",
})`
  margin-bottom: 8px;
`;

const ItemText = styled.div.attrs({
  className: "body-text-large regular neutral-900",
})`
  white-space: pre-wrap; /* this will render the newline character to show new line */
`;

const ItemInfo = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
`;

const ItemInfoSecftion = styled.div`
  display: flex;
  gap: 8px;
`;

const ItemInfoLabel = styled.div.attrs({
  className: "body-text-regular regular neutral-600",
})``;

export default ViewItem;
